import React, { useEffect, useRef, useState } from "react"
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Image,
  Input,
  Popconfirm,
  Row,
  Space,
  Tag,
  Typography,
  Upload,
  message,
} from "antd"
import {
  DeleteOutlined,
  DownloadOutlined,
  CloseCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons"
import { useStoreState, StateMapper } from "easy-peasy"
import { swimminglyApi } from "./utils"
import { AppDataStore } from "../appData/types"

const { Text, Title } = Typography
const TEAM_LOGOS_CHANGED_EVENT = "swimmingly:team-logos:changed"

const SLOT_DEFINITIONS = [
  {
    slotIndex: 0,
    label: "Primary logo",
    helper:
      "Available to choose in Report Studio after approval.",
  },
  {
    slotIndex: 1,
    label: "Secondary logo",
    helper:
      "Available to choose in Report Studio after approval.",
  },
  {
    slotIndex: 2,
    label: "Alternate logo",
    helper:
      "Available to choose in Report Studio after approval.",
  },
]

const thumbnailStageStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  aspectRatio: "4 / 3",
  padding: 16,
  borderRadius: 12,
  overflow: "hidden" as const,
  background:
    "radial-gradient(circle at top, rgba(37,191,234,0.12), rgba(255,255,255,0.95))",
}

const thumbnailImageStyle = {
  width: "100%",
  height: 128,
  maxWidth: "100%",
  objectFit: "contain" as const,
}

const slotDraggerStyle = {
  background: "transparent",
  border: "none",
  padding: 0,
}

function getSlotPreviewFrameStyle(
  state: "empty" | "queued" | "filled",
): Record<string, string | number> {
  return {
    ...thumbnailStageStyle,
    border:
      state === "empty"
        ? "2px dashed rgba(15,94,156,0.35)"
        : "1px solid rgba(37,191,234,0.18)",
  }
}

const slotCardStyle = {
  height: 560,
  borderRadius: 16,
  border: "1px solid rgba(37,191,234,0.18)",
  background: "#fff",
}

const slotCardBodyStyle = {
  padding: 20,
  height: "100%",
  display: "flex",
  flexDirection: "column" as const,
}

const slotCardInnerStyle = {
  display: "flex",
  flexDirection: "column" as const,
  gap: 12,
  height: "100%",
  width: "100%",
}

const slotMetaAreaStyle = {
  display: "flex",
  flexDirection: "column" as const,
  gap: 12,
  minHeight: 0,
  flex: 1,
  overflow: "hidden" as const,
}

const slotFooterTextStyle = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical" as const,
  WebkitLineClamp: 2,
  overflow: "hidden" as const,
}

type TeamLogo = {
  id: number
  displayName: string
  originalFileName: string
  cdnUrl: string
  mimeType: string
  fileSizeBytes: number
  imageWidth: number | null
  imageHeight: number | null
  longestSidePx: number | null
  status: "pending" | "approved" | "rejected"
  reviewSource: "none" | "automation" | "manual"
  automationDecision:
    | "pending"
    | "approved"
    | "rejected"
    | "flagged"
    | "skipped"
    | "error"
  automationSummary: string | null
  rejectionReason: string | null
  createdAt: string
  slotIndex: number
}

function formatBytes(bytes: number) {
  if (!bytes) return "0 B"
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function statusTag(status: TeamLogo["status"]) {
  if (status === "approved") return <Tag color="green">Approved</Tag>
  if (status === "rejected") return <Tag color="red">Rejected</Tag>
  return <Tag color="gold">Pending review</Tag>
}

function automationTag(decision: TeamLogo["automationDecision"]) {
  const colorByDecision: Record<string, string> = {
    approved: "green",
    rejected: "red",
    flagged: "orange",
    skipped: "blue",
    error: "default",
    pending: "gold",
  }
  return <Tag color={colorByDecision[decision] || "default"}>{decision}</Tag>
}

function shouldShowAutomationTag(logo: TeamLogo) {
  if (
    (logo.automationDecision === "approved" && logo.status === "approved") ||
    (logo.automationDecision === "rejected" && logo.status === "rejected")
  ) {
    return false
  }

  return true
}

function getDefaultDisplayName(fileName: string) {
  return fileName.replace(/\.[^.]+$/, "").trim()
}

async function readLongestSide(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new window.Image()
    img.onload = () => {
      const longestSide = Math.max(img.naturalWidth || 0, img.naturalHeight || 0)
      URL.revokeObjectURL(url)
      resolve(longestSide)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("We could not read this file's dimensions."))
    }
    img.src = url
  })
}

async function validateBeforeUpload(file: File) {
  const allowedTypes = ["image/svg+xml", "image/png", "image/jpeg"]
  if (!allowedTypes.includes(file.type)) {
    throw new Error("Only SVG, PNG, and JPG files are supported.")
  }

  const longestSide = await readLongestSide(file)
  if (longestSide < 400) {
    throw new Error(
      "This logo is too small. Please upload a file that is at least 400px on its longest side.",
    )
  }
}

export default function TeamLogosManager({
  clubId,
  uploadIntent,
}: {
  clubId: number
  uploadIntent?: {
    key: string
    source: string | null
    requestedSlotIndex: number | null
  } | null
}): JSX.Element {
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const [logos, setLogos] = useState<TeamLogo[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [displayName, setDisplayName] = useState("")
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [pendingSlotIndex, setPendingSlotIndex] = useState<number | null>(null)
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null)
  const [uploadFeedback, setUploadFeedback] = useState<{
    type: "success" | "info" | "warning"
    message: string
  } | null>(null)
  const [highlightedSlotIndex, setHighlightedSlotIndex] = useState<number | null>(null)
  const slotRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const lastHandledUploadIntentKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!pendingFile) {
      setPendingPreviewUrl(null)
      return undefined
    }

    const objectUrl = URL.createObjectURL(pendingFile)
    setPendingPreviewUrl(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [pendingFile])

  const loadLogos = async () => {
    setLoading(true)
    const response: any = await swimminglyApi.get(`/api/team-logos/club/${clubId}`)
    if (response?.status === "error") {
      message.error(response.message || "Unable to load team logos")
      setLoading(false)
      return
    }
    setLogos(Array.isArray(response?.logos) ? response.logos : [])
    setLoading(false)
  }

  useEffect(() => {
    loadLogos()
  }, [clubId])

  useEffect(() => {
    if (!uploadIntent?.key) return
    if (loading) return
    if (lastHandledUploadIntentKeyRef.current === uploadIntent.key) return

    const occupiedSlotIndexes = new Set(logos.map((logo) => logo.slotIndex))
    const requestedSlotIndex =
      uploadIntent.requestedSlotIndex !== null &&
      uploadIntent.requestedSlotIndex >= 0 &&
      uploadIntent.requestedSlotIndex <= 2
        ? uploadIntent.requestedSlotIndex
        : null
    const firstEmptySlotIndex = SLOT_DEFINITIONS.find(
      ({ slotIndex }) => !occupiedSlotIndexes.has(slotIndex),
    )?.slotIndex
    const targetSlotIndex =
      requestedSlotIndex !== null && !occupiedSlotIndexes.has(requestedSlotIndex)
        ? requestedSlotIndex
        : firstEmptySlotIndex ?? requestedSlotIndex ?? 0

    lastHandledUploadIntentKeyRef.current = uploadIntent.key
    setHighlightedSlotIndex(targetSlotIndex)

    if (firstEmptySlotIndex !== undefined) {
      setUploadFeedback({
        type: "info",
        message:
          targetSlotIndex === 0
            ? "Start by uploading your primary team logo."
            : `Start by uploading your ${SLOT_DEFINITIONS.find((slot) => slot.slotIndex === targetSlotIndex)?.label.toLowerCase() || "team logo"}.`,
      })
    }

    window.requestAnimationFrame(() => {
      const targetElement = slotRefs.current[targetSlotIndex]
      targetElement?.scrollIntoView({ behavior: "smooth", block: "center" })
      targetElement?.focus()

      if (typeof targetElement?.animate === "function") {
        targetElement.animate(
          [
            {
              transform: "translateY(0) scale(1)",
              boxShadow: "0 0 0 0 rgba(37,191,234,0)",
            },
            {
              transform: "translateY(-3px) scale(1.01)",
              boxShadow: "0 0 0 8px rgba(37,191,234,0.18)",
            },
            {
              transform: "translateY(0) scale(1)",
              boxShadow: "0 0 0 0 rgba(37,191,234,0)",
            },
          ],
          {
            duration: 950,
            easing: "cubic-bezier(0.22, 1, 0.36, 1)",
            iterations: 1,
          },
        )
      }
    })
  }, [loading, logos, uploadIntent])

  const dispatchTeamLogosChanged = () => {
    window.dispatchEvent(
      new CustomEvent(TEAM_LOGOS_CHANGED_EVENT, {
        detail: { clubId },
      }),
    )
  }

  const handleDownload = async (logoId: number) => {
    try {
      const response = await fetch(`/api/team-logos/${logoId}/download`, {
        credentials: "same-origin",
      })

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`)
      }

      const blob = await response.blob()
      const contentDisposition = response.headers.get("Content-Disposition") || ""
      const filenameMatch = contentDisposition.match(/filename="?([^\"]+)"?/) 
      const fileName = filenameMatch?.[1] || `team-logo-${logoId}`
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = objectUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(objectUrl)
    } catch (_err) {
      message.error("Unable to download this logo file")
    }
  }

  const handleDelete = async (logoId: number) => {
    const response: any = await swimminglyApi
      .delete(`/api/team-logos/${logoId}`)
      .body({ userId: user?.userId })
    if (response?.status === "error") {
      message.error(response.message || "Unable to delete the logo")
      return
    }

    message.success("Logo deleted")
    setLogos((prev) => prev.filter((logo) => logo.id !== logoId))
    dispatchTeamLogosChanged()
    window.dispatchEvent(new Event("swimmingly:activity-feed:refetch"))
  }

  const clearPendingFile = () => {
    setPendingFile(null)
    setPendingSlotIndex(null)
    setDisplayName("")
  }

  const handleQueueFile = async (file: File, slotIndex: number) => {
    if (slotIndex < 0 || slotIndex > 2) {
      message.error("Choose a valid logo slot.")
      return Upload.LIST_IGNORE
    }

    const existingLogo = logos.find((logo) => logo.slotIndex === slotIndex)
    if (logos.length >= 3 && !existingLogo) {
      message.error("You can only keep up to 3 team logos at a time.")
      return Upload.LIST_IGNORE
    }

    try {
      await validateBeforeUpload(file)
      setPendingFile(file)
      setPendingSlotIndex(slotIndex)
      setDisplayName(getDefaultDisplayName(file.name))
      setHighlightedSlotIndex(slotIndex)
      setUploadFeedback(null)
    } catch (err: any) {
      message.error(err?.message || "Unable to use this file")
    }

    return Upload.LIST_IGNORE
  }

  const handleUploadPendingFile = async () => {
    const file = pendingFile
    if (!file) {
      message.error("Choose a file before uploading.")
      return
    }
    if (pendingSlotIndex === null) {
      message.error("Choose a logo slot before uploading.")
      return
    }

    if (!displayName.trim()) {
      message.error("Give this logo a name before uploading it.")
      return
    }

    const existingLogo = logos.find((logo) => logo.slotIndex === pendingSlotIndex)
    if (logos.length >= 3 && !existingLogo) {
      message.error("You can only keep up to 3 team logos at a time.")
      return
    }

    try {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("displayName", displayName.trim())
      formData.append("slotIndex", String(pendingSlotIndex))
      if (user?.userId) {
        formData.append("userId", String(user.userId))
      }

      setUploading(true)
      const response: any = await swimminglyApi
        .post(`/api/team-logos/club/${clubId}/upload`)
        .body(formData)

      if (response?.status === "error") {
        message.error(response.message || "Unable to upload team logo")
        setUploading(false)
        return
      }

      message.success("Logo uploaded")
      setHighlightedSlotIndex(response?.logo?.slotIndex ?? null)
      if (response?.logo?.status === "approved") {
        setUploadFeedback({
          type: "success",
          message:
            "Logo uploaded and auto-approved. It is now available for approved use cases.",
        })
      } else if (response?.logo?.automationDecision === "skipped") {
        setUploadFeedback({
          type: "info",
          message:
            "Logo uploaded and sent to manual review. SVG uploads stay pending until a Swimmingly admin approves them.",
        })
      } else {
        setUploadFeedback({
          type: "warning",
          message:
            "Logo uploaded and is pending review. It is only visible to your club admins until approval.",
        })
      }
      clearPendingFile()
      setLogos((prev) => {
        const next = prev.filter((logo) => logo.slotIndex !== response.logo.slotIndex)
        next.push(response.logo)
        return next.sort((left, right) => left.slotIndex - right.slotIndex)
      })
      dispatchTeamLogosChanged()
      window.dispatchEvent(new Event("swimmingly:activity-feed:refetch"))
    } catch (err: any) {
      message.error(err?.message || "Unable to upload team logo")
    } finally {
      setUploading(false)
    }
  }

  const logosBySlot = SLOT_DEFINITIONS.map(({ slotIndex, label, helper }) => ({
    slotIndex,
    label,
    helper,
    logo: logos.find((entry) => entry.slotIndex === slotIndex) || null,
    isPending: pendingSlotIndex === slotIndex,
  }))

  return (
    <div>
      <div
        style={{
          padding: 20,
          marginBottom: 12,
          borderRadius: 16,
          background:
            "linear-gradient(135deg, rgba(37,191,234,0.12), rgba(15,94,156,0.08))",
          border: "1px solid rgba(37,191,234,0.18)",
        }}
      >
        <Space orientation="vertical" size={8} style={{ width: "100%" }}>
          <Title level={4} style={{ marginBottom: 0 }}>
            Upload Team Logos
          </Title>
          <Text type="secondary">
            Brand your club&apos;s reports with approved team logos. Your club can choose
            which logo to use on Results PDFs, Lineup Reports, and Heat Sheets from
            Report Studio. SVG is best. PNG and JPG also work. WebP is not supported.
          </Text>
          <Space wrap>
            <Tag color="blue">SVG preferred</Tag>
            <Tag color="cyan">PNG + JPG supported</Tag>
            <Tag color="gold">400px+ on longest side</Tag>
            <Tag color="purple">Square is helpful, not required</Tag>
            <Tag color="default">{logos.length} / 3 uploaded</Tag>
          </Space>
          <Alert
            type="info"
            showIcon
            title="Approved logos can brand Results PDFs, Lineup Reports, and Heat Sheets. Pending logos are only visible to your club admins until approval."
          />
        </Space>
      </div>

      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        {uploadFeedback ? (
          <Alert
            type={uploadFeedback.type}
            showIcon
            title={uploadFeedback.message}
            closable={{ onClose: () => setUploadFeedback(null) }}
          />
        ) : null}
        {loading ? <Card loading variant="borderless" /> : null}
        <Row gutter={[16, 16]}>
          {logosBySlot.map(({ slotIndex, label, helper, logo, isPending }) => (
            <Col xs={24} md={12} xl={8} key={slotIndex}>
              <div
                ref={(element) => {
                  slotRefs.current[slotIndex] = element
                }}
                tabIndex={-1}
                style={{
                  borderRadius: 16,
                  outline: "none",
                  transition:
                    "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
                  transformOrigin: "center top",
                }}
              >
                {!logo && !isPending ? (
                  <Upload.Dragger
                    accept=".svg,.png,.jpg,.jpeg"
                    multiple={false}
                    beforeUpload={(file) => handleQueueFile(file as File, slotIndex)}
                    showUploadList={false}
                    disabled={uploading}
                    style={slotDraggerStyle}
                  >
                    <Card
                      variant="borderless"
                      style={{
                        ...slotCardStyle,
                        border:
                          highlightedSlotIndex === slotIndex
                            ? "2px solid rgba(15,94,156,0.55)"
                            : slotCardStyle.border,
                        boxShadow:
                          highlightedSlotIndex === slotIndex
                            ? "0 0 0 4px rgba(37,191,234,0.16), 0 12px 28px rgba(15,94,156,0.12)"
                            : undefined,
                      }}
                      styles={{ body: slotCardBodyStyle }}
                    >
                      <div style={slotCardInnerStyle}>
                        <Space style={{ width: "100%", justifyContent: "space-between" }}>
                          <Text strong>{label}</Text>
                          <Space size={8}>
                            {highlightedSlotIndex === slotIndex ? (
                              <Tag color="blue">Recommended</Tag>
                            ) : null}
                            <Tag>Empty</Tag>
                          </Space>
                        </Space>
                        <div style={slotMetaAreaStyle}>
                          <Text type="secondary" style={slotFooterTextStyle}>
                            {helper}
                          </Text>
                          <div style={getSlotPreviewFrameStyle("empty")}>
                            <div
                              style={{
                                width: 96,
                                height: 96,
                                borderRadius: 20,
                                border: "2px dashed rgba(15,94,156,0.35)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "rgba(15,94,156,0.85)",
                                fontSize: 28,
                              }}
                            >
                              <UploadOutlined />
                            </div>
                          </div>
                          <Text strong style={{ textAlign: "center" }}>
                            No logo in this slot
                          </Text>
                          <Text
                            type="secondary"
                            style={{ textAlign: "center", ...slotFooterTextStyle }}
                          >
                            Drag a file into this box or click below to upload this {label.toLowerCase()}.
                          </Text>
                          <Button
                            type={highlightedSlotIndex === slotIndex ? "primary" : "dashed"}
                            icon={<UploadOutlined />}
                            style={{ marginTop: "auto" }}
                          >
                            Upload Team Logo
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </Upload.Dragger>
                ) : (
                  <Card
                    variant="borderless"
                    style={{
                      ...slotCardStyle,
                      border:
                        highlightedSlotIndex === slotIndex
                          ? "2px solid rgba(15,94,156,0.55)"
                          : slotCardStyle.border,
                      boxShadow:
                        highlightedSlotIndex === slotIndex
                          ? "0 0 0 4px rgba(37,191,234,0.16), 0 12px 28px rgba(15,94,156,0.12)"
                          : undefined,
                    }}
                    styles={{ body: slotCardBodyStyle }}
                  >
                  <div style={slotCardInnerStyle}>
                    <Space style={{ width: "100%", justifyContent: "space-between" }}>
                      <Text strong>{label}</Text>
                      <Space size={8}>
                        {highlightedSlotIndex === slotIndex ? (
                          <Tag color="blue">Recommended</Tag>
                        ) : null}
                        {isPending ? <Tag>Queued</Tag> : null}
                      </Space>
                    </Space>

                    {isPending && pendingFile ? (
                      <div style={slotMetaAreaStyle}>
                        <Text type="secondary" style={slotFooterTextStyle}>
                          {helper}
                        </Text>
                        {pendingPreviewUrl ? (
                          <div style={getSlotPreviewFrameStyle("queued")}>
                            <Image
                              src={pendingPreviewUrl}
                              alt={pendingFile.name}
                              style={thumbnailImageStyle}
                              preview={false}
                            />
                          </div>
                        ) : null}
                        <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
                          <Space
                            style={{ width: "100%", justifyContent: "space-between" }}
                            align="start"
                          >
                            <Space orientation="vertical" size={2}>
                              <Text strong>Ready to upload</Text>
                              <Text type="secondary" style={slotFooterTextStyle}>
                                {pendingFile.name}
                              </Text>
                              <Text type="secondary">{formatBytes(pendingFile.size)}</Text>
                            </Space>
                            <Button
                              type="text"
                              icon={<CloseCircleOutlined />}
                              onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                clearPendingFile()
                              }}
                              disabled={uploading}
                            >
                              Clear
                            </Button>
                          </Space>

                          <Input
                            placeholder="Give this upload a clear name"
                            value={displayName}
                            onChange={(event) => setDisplayName(event.target.value)}
                            maxLength={255}
                            disabled={uploading}
                            onClick={(event) => event.stopPropagation()}
                          />
                          <Text type="secondary" style={slotFooterTextStyle}>
                            This name is prefilled from the file name and can be changed before upload.
                          </Text>
                          <Space style={{ marginTop: "auto" }}>
                            <Button
                              type="primary"
                              icon={<UploadOutlined />}
                              onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                handleUploadPendingFile()
                              }}
                              loading={uploading}
                            >
                              Upload Team Logo
                            </Button>
                            <Button
                              onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                clearPendingFile()
                              }}
                              disabled={uploading}
                            >
                              Cancel
                            </Button>
                          </Space>
                        </div>
                      </div>
                    ) : logo ? (
                      <div style={slotMetaAreaStyle}>
                        <Text type="secondary" style={slotFooterTextStyle}>
                          {helper}
                        </Text>
                        <div style={getSlotPreviewFrameStyle("filled")}>
                          <Image
                            src={logo.cdnUrl}
                            alt={logo.displayName}
                            style={thumbnailImageStyle}
                            preview
                          />
                        </div>
                        <div>
                          <Text strong>{logo.displayName}</Text>
                          <br />
                          <Text type="secondary" style={slotFooterTextStyle}>
                            {logo.originalFileName}
                          </Text>
                        </div>
                        <Space wrap>
                          {statusTag(logo.status)}
                          {shouldShowAutomationTag(logo)
                            ? automationTag(logo.automationDecision)
                            : null}
                          <Tag>{formatBytes(logo.fileSizeBytes)}</Tag>
                          {logo.longestSidePx ? <Tag>{logo.longestSidePx}px</Tag> : null}
                        </Space>
                        {logo.status === "pending" ? (
                          <Alert
                            type="warning"
                            showIcon
                            title="Only visible to your club admins while pending."
                          />
                        ) : null}
                        {logo.status === "rejected" && logo.rejectionReason ? (
                          <Alert type="error" showIcon title={logo.rejectionReason} />
                        ) : null}
                        {logo.automationSummary && logo.status !== "approved" ? (
                          <Text type="secondary" style={slotFooterTextStyle}>
                            {logo.automationSummary}
                          </Text>
                        ) : null}
                        <Space style={{ marginTop: "auto" }}>
                          <Button
                            icon={<DownloadOutlined />}
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              handleDownload(logo.id)
                            }}
                          >
                            Download
                          </Button>
                          <Upload
                            accept=".svg,.png,.jpg,.jpeg"
                            multiple={false}
                            beforeUpload={(file) => handleQueueFile(file as File, slotIndex)}
                            showUploadList={false}
                            disabled={uploading}
                          >
                            <Button
                              type="dashed"
                              icon={<UploadOutlined />}
                              disabled={uploading}
                            >
                              Replace
                            </Button>
                          </Upload>
                          <Popconfirm
                            title="Delete this logo?"
                            description="This removes the logo from this slot."
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            onConfirm={(event) => {
                              event?.stopPropagation?.()
                              handleDelete(logo.id)
                            }}
                            onPopupClick={(event) => event.stopPropagation()}
                          >
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                              }}
                            />
                          </Popconfirm>
                        </Space>
                      </div>
                    ) : null}
                  </div>
                  </Card>
                )}
              </div>
            </Col>
          ))}
        </Row>
      </Space>

    </div>
  )
}